// skipcq: JS-W1028
import { useCallback, useMemo } from 'react';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardActions from '@mui/material/CardActions';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import PropTypes from 'prop-types';

import BeerTagChips from '../brewhouse/BeerTagChips';
import PrimaryActionButton from '../common/PrimaryActionButton';
import TapRatingBar from '../ratings/TapRatingBar';
import { getTopBeerTagLabels } from '../../utils/brewhouseBeerStats';
import { getPortfolioBeerListingLabels } from '../../utils/brewhousePortfolio/formatPortfolioBeerListingSummary';
import { getGlassAssetPathForBeer } from '../../utils/brewhousePortfolio/beerStyleGlassMapping';
import PortfolioBeerLocationList from './PortfolioBeerLocationList';

function formatAbv(abv) {
  if (typeof abv !== 'number' || !Number.isFinite(abv)) {
    return '—';
  }
  return `${abv}% ABV`;
}

function PintPortfolioBeerCard({
  beer,
  styleMetaMap,
  tagLabelMap,
  breweryNameById = {},
  onViewDetails,
  highlighted = false,
}) {
  const handleViewDetailsClick = useCallback(() => {
    onViewDetails(beer);
  }, [beer, onViewDetails]);

  const styleId = typeof beer.style === 'string' ? beer.style : '';
  const styleLabel =
    styleId.length > 0 && styleMetaMap[styleId]?.name
      ? styleMetaMap[styleId].name
      : styleId.length > 0
        ? styleId
        : '—';
  const glassPath = getGlassAssetPathForBeer(beer.style, styleMetaMap);
  const metadataLine = `${styleLabel} · ${formatAbv(beer.abv)}`;
  const tagEntries = useMemo(
    () => getTopBeerTagLabels(beer.tags, tagLabelMap),
    [beer.tags, tagLabelMap],
  );
  const listingLocationLabels = useMemo(
    () => getPortfolioBeerListingLabels(beer, breweryNameById),
    [beer, breweryNameById],
  );

  return (
    <Card
      data-beer-id={beer.id}
      tabIndex={-1}
      variant="outlined"
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: 'background.paper',
        outline: highlighted ? (theme) => `2px solid ${theme.palette.primary.main}` : 'none',
        outlineOffset: highlighted ? 2 : 0,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          py: 1.5,
          px: 2,
          bgcolor: 'grey.50',
          minHeight: 88,
        }}
      >
        <Box
          component="img"
          src={glassPath}
          alt=""
          sx={{ maxHeight: 68, maxWidth: '100%', objectFit: 'contain' }}
        />
      </Box>
      <CardContent sx={{ flexGrow: 1, py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
        <Typography
          variant="subtitle1"
          component="h3"
          sx={{ fontFamily: 'Lora, Georgia, serif', fontWeight: 600 }}
        >
          {beer.name || 'Untitled beer'}
        </Typography>
        <Box sx={{ mt: 0.75 }}>
          <TapRatingBar
            rating={beer.ratings?.overall}
            size="compact"
            showNumeric
            showUnratedLabel
          />
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
          {metadataLine}
        </Typography>
        <PortfolioBeerLocationList
          locationLabels={listingLocationLabels}
          compact
        />
        <BeerTagChips tags={tagEntries} />
      </CardContent>
      <CardActions sx={{ px: 2, pb: 2, pt: 0 }}>
        <PrimaryActionButton
          size="small"
          fullWidth
          onClick={handleViewDetailsClick}
          aria-label={`View details for ${beer.name || 'beer'}`}
        >
          View details
        </PrimaryActionButton>
      </CardActions>
    </Card>
  );
}

PintPortfolioBeerCard.propTypes = {
  beer: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    style: PropTypes.string,
    abv: PropTypes.number,
    ratings: PropTypes.shape({
      overall: PropTypes.number,
    }),
    tags: PropTypes.object,
  }).isRequired,
  styleMetaMap: PropTypes.object.isRequired,
  tagLabelMap: PropTypes.objectOf(PropTypes.string).isRequired,
  breweryNameById: PropTypes.objectOf(PropTypes.string),
  onViewDetails: PropTypes.func.isRequired,
  highlighted: PropTypes.bool,
};

export default PintPortfolioBeerCard;
